import { useId } from 'react';
import { CircleHelp } from 'lucide-react';
import type { Museum, MuseumDataset } from '../data/types';
import { useT } from '../hooks/useT';
import { vervalDatum } from '../utils/kaart';
import type { KaartInfo, StatusMap } from '../utils/museum';
import { datumKort } from '../utils/datum';
import { chip } from '../utils/ui';
import { BackupSectie } from './BackupSectie';
import { Legenda } from './Legenda';

const invoer =
  'min-h-12 w-full rounded-2xl border-none bg-black/5 px-4 text-base outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 dark:bg-slate-800/80';

/** App-specifieke onderdelen in het zijmenu: kaartgegevens, legenda en bronvermelding. */
interface MenuExtraProps {
  kaart: KaartInfo;
  setKaart: (k: KaartInfo) => void;
  dataset: MuseumDataset;
  musea: Museum[];
  status: StatusMap;
  laatsteBackup: string | null;
  onBackupGemaakt: (datum: string) => void;
  onMelding: (tekst: string) => void;
  onHerstel: () => void;
  onUitleg: () => void;
}

export function MenuExtra({ kaart, setKaart, dataset, musea, status, laatsteBackup, onBackupGemaakt, onMelding, onHerstel, onUitleg }: MenuExtraProps) {
  const { t, tf, language } = useT();
  const prijsId = useId();
  const vanafId = useId();

  return (
    <>
      <section className="space-y-3 border-t border-black/5 pt-6 dark:border-white/10">
        <h3 className="font-semibold">{t('kaartTitel')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <label htmlFor={prijsId} className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
            <span className="block whitespace-nowrap">{t('kaartPrijs')}</span>
            <input
              id={prijsId}
              key={kaart.prijs}
              aria-label={t('kaartPrijs')}
              type="number"
              inputMode="decimal"
              min={0}
              max={500}
              step={0.05}
              defaultValue={(kaart.prijs / 100).toFixed(2)}
              onBlur={(e) => {
                const n = Number(e.target.value.replace(',', '.'));
                if (Number.isFinite(n) && n >= 0 && n <= 500) setKaart({ ...kaart, prijs: Math.round(n * 100) });
              }}
              className={`${invoer} text-slate-900 dark:text-slate-100`}
            />
          </label>
          <label htmlFor={vanafId} className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
            <span className="block whitespace-nowrap">{t('kaartVanaf')}</span>
            <input
              id={vanafId}
              aria-label={t('kaartVanaf')}
              type="date"
              value={kaart.vanaf ?? ''}
              onChange={(e) => {
                if (e.target.value === '') setKaart({ ...kaart, vanaf: null });
                else if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && e.target.value >= '2000-01-01') setKaart({ ...kaart, vanaf: e.target.value });
              }}
              className={`${invoer} text-slate-900 dark:text-slate-100`}
            />
          </label>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t('kaartHulp')}</p>
        {kaart.vanaf && (
          <p className="text-sm font-semibold">
            {tf('kaartGeldigTot', { datum: datumKort(vervalDatum(kaart.vanaf), language) })}
            <span className="font-normal text-slate-600 dark:text-slate-400"> · {t('kaartControle')}</span>
          </p>
        )}
      </section>

      <BackupSectie
        musea={musea}
        status={status}
        kaart={kaart}
        laatsteBackup={laatsteBackup}
        onBackupGemaakt={onBackupGemaakt}
        onMelding={onMelding}
        onHerstel={onHerstel}
      />

      <section className="space-y-3 border-t border-black/5 pt-6 dark:border-white/10">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold">{t('legendaTitel')}</h3>
          <button onClick={onUitleg} className={chip(false)}>
            <CircleHelp size={18} aria-hidden /> {t('uitlegKnop')}
          </button>
        </div>
        <Legenda />
      </section>

      <section className="space-y-2 border-t border-black/5 pt-6 text-sm text-slate-600 dark:border-white/10 dark:text-slate-400">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t('bronTitel')}</h3>
        {dataset.voorbeeld && (
          <p className="rounded-2xl bg-amber-100/90 p-3 text-amber-950 dark:bg-amber-950/60 dark:text-amber-100">
            {tf('bronVoorbeeld', { n: dataset.musea.length })}
          </p>
        )}
        <p>{tf('bronTekst', { bron: dataset.bron, datum: datumKort(dataset.bijgewerkt, language) })}</p>
        <p>{t('bronOnofficieel')}</p>
      </section>
    </>
  );
}
